import { useState, useEffect, useRef } from "react";
import moment from "moment-timezone";
import toastError from "../../../errors/toastError";
import { listContactListsSimple, getContactList } from "../../../services/contactLists.api";
import { listFiles } from "../../../services/files.api";
import { getCampaign, getLastActiveCampaign } from "../../../services/campaigns.api";
import { listTags } from "../../../services/tags.api";
import { listWhatsapps } from "../../../services/whatsapp.api";
import { listPrompts } from "../../../services/prompt.api";
import {
  getCampaignTimezone,
  getCampaignTimezoneSync,
  setCampaignTimezoneCache,
} from "../../../helpers/getCampaignTimezone";

const makeInitialState = (companyId, campaignType) => ({
  name: "",
  message1: "",
  message2: "",
  message3: "",
  message4: "",
  message5: "",
  confirmationMessage1: "",
  confirmationMessage2: "",
  confirmationMessage3: "",
  confirmationMessage4: "",
  confirmationMessage5: "",
  status: "INATIVA",
  confirmation: false,
  scheduledAt: "",
  whatsappId: "",
  contactListId: "",
  tagListId: "",
  companyId,
  // ✅ FASE 4: TIPO DE CAMPAÑA
  tipo: campaignType || 'UNICA',
  // ✅ CAMPOS DE RECURRENCIA
  isRecurring: campaignType === 'RECURRENTE',
  recurrenceType: "",
  recurrenceInterval: "",
  recurrenceDaysOfWeek: "",
  recurrenceDayOfMonth: "",
  recurrenceEndDate: "",
  maxExecutions: "",
  // ✅ CAMPOS DE INTERVALOS
  messageInterval: 45,
  longerIntervalAfter: 20,
  greaterInterval: 60,
  // ✅ CAMPOS DE ETIQUETADO AUTOMÁTICO (independiente de tagListId)
  autoTagEnabled: false,
  autoTagId: null,
  promptId: "",
  replyPromptId: "",
});

const useCampaignData = ({
  companyId,
  campaignId,
  open,
  campaignType,
  initialValues,
  campaignAiChoice,
  setCampaignAiChoice,
  onFormKeyIncrement,
  onResetAttachment,
  onSetMessageTab,
  onSetConfigTab,
  onSetWizardLocalChoice,
}) => {
  const isMounted = useRef(true);

  const [campaign, setCampaign] = useState(() => makeInitialState(companyId, campaignType));
  const [contactLists, setContactLists] = useState([]);
  const [whatsapps, setWhatsapps] = useState([]);
  const [tagLists, setTagLists] = useState([]);
  const [companyTimezone, setCompanyTimezone] = useState(getCampaignTimezoneSync());
  const [promptsList, setPromptsList] = useState([]);
  const [file, setFile] = useState(null);
  const [selectedContactListCount, setSelectedContactListCount] = useState(null);

  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  // ✅ SINCRONIZAR ZONA HORARIA AL ABRIR EL MODAL
  useEffect(() => {
    if (open && companyId) {
      getCampaignTimezone(companyId)
        .then((timezone) => {
          setCompanyTimezone(timezone);
          setCampaignTimezoneCache(timezone);
        })
        .catch((error) => {
          console.warn("[CampaignModal] Error obteniendo zona horaria, usando cache:", error);
          const cached = getCampaignTimezoneSync();
          setCompanyTimezone(cached);
        });
    }
  }, [open, companyId]);

  // ✅ CARGA DE PROMPTS (solo en modo IA activo)
  useEffect(() => {
    if (!open || campaignAiChoice !== "yes") {
      setPromptsList([]);
      return undefined;
    }
    let cancelled = false;
    listPrompts()
      .then(({ data }) => {
        if (!cancelled && isMounted.current) {
          setPromptsList(Array.isArray(data.prompts) ? data.prompts : []);
        }
      })
      .catch((err) => {
        if (!cancelled && isMounted.current) toastError(err);
      });
    return () => {
      cancelled = true;
    };
  }, [open, campaignAiChoice]);

  // ✅ CARGA DE LISTA DE ARCHIVOS
  useEffect(() => {
    (async () => {
      try {
        const { data } = await listFiles({ companyId });
        setFile(data.files);
      } catch (err) {
        toastError(err);
      }
    })();
  }, [companyId]);

  // ✅ CARGA DE RECURSOS: contactLists, whatsapps, tags
  useEffect(() => {
    if (open && isMounted.current) {
      listContactListsSimple({ companyId })
        .then(({ data }) => setContactLists(data));

      listWhatsapps({ companyId, session: 0 })
        .then(({ data }) => setWhatsapps(data));

      listTags({ companyId })
        .then(({ data }) => {
          const fetchedTags = data.tags;
          const formattedTagLists = fetchedTags.map((tag) => ({
            id: tag.id,
            name: tag.name,
          }));
          setTagLists(formattedTagLists);
        })
        .catch((error) => {
          console.error("Error retrieving tags:", error);
        });
    }
  }, [open, companyId]);

  // ✅ CARGA DE CAMPAÑA (nueva o edición) + sugerencia de fecha
  useEffect(() => {
    if (isMounted.current) {
      // ✅ LIMPIEZA COMPLETA: Si es nueva campaña (sin campaignId), resetear TODO
      if (!campaignId && open) {
        setCampaign(makeInitialState(companyId, campaignType));
        setCampaignAiChoice(undefined);
        onSetWizardLocalChoice("");
        onResetAttachment();
        setSelectedContactListCount(null);
        onSetMessageTab(0);
        onSetConfigTab(0);
        // Limpiar input de archivo y adjuntos
        // Tras reset de `campaign`, remontar Formik (enableReinitialize=false)
        setTimeout(() => {
          if (isMounted.current) onFormKeyIncrement();
        }, 0);
      }

      if (initialValues) {
        setCampaign((prevState) => {
          return { ...prevState, ...initialValues };
        });
      }

      // ✅ MEJORA: Obtener última campaña activa para sugerir fecha programada
      if (!campaignId) {
        // ✅ CORRECCIÓN: Usar zona horaria sincronizada desde Settings
        const timezone = getCampaignTimezoneSync();

        getLastActiveCampaign()
          .then(({ data: lastActiveCampaign }) => {
            if (lastActiveCampaign && (lastActiveCampaign.scheduledAt || lastActiveCampaign.nextScheduledAt)) {
              // Usar nextScheduledAt si existe (recurrentes), sino scheduledAt
              const lastDate = lastActiveCampaign.nextScheduledAt || lastActiveCampaign.scheduledAt;
              if (lastDate) {
                // ✅ CORRECCIÓN: Interpretar lastDate en la zona horaria de la empresa
                // El backend devuelve fechas como strings "YYYY-MM-DD HH:mm:ss" en zona empresa
                let lastMoment = moment.tz(String(lastDate), 'YYYY-MM-DD HH:mm:ss', timezone);
                if (!lastMoment.isValid()) {
                  lastMoment = moment.tz(String(lastDate), timezone);
                }

                // Calcular nueva fecha: última fecha + 30 minutos (en zona empresa)
                let newDate = lastMoment.add(30, 'minutes');

                // Validar que no sea en el pasado (comparar en zona empresa)
                const nowInCompanyTimezone = moment().tz(timezone);
                if (newDate.isBefore(nowInCompanyTimezone)) {
                  // Si está en el pasado, usar ahora + 30 min (en zona empresa)
                  newDate = nowInCompanyTimezone.add(30, 'minutes');
                }

                // ✅ CORRECCIÓN: DateTimePickerWithTimezone espera formato "YYYY-MM-DD HH:mm:ss"
                // Formatear directamente en zona empresa (sin conversión)
                const formattedDate = newDate.format('YYYY-MM-DD HH:mm:ss');

                // Establecer en estado
                setCampaign((prevState) => ({
                  ...prevState,
                  scheduledAt: formattedDate
                }));
              }
            } else {
              // No hay campañas activas, usar fecha actual + 30 min EN ZONA EMPRESA
              const nowInCompanyTimezone = moment().tz(timezone);
              const newDate = nowInCompanyTimezone.add(30, 'minutes');
              // ✅ CORRECCIÓN: DateTimePickerWithTimezone espera formato "YYYY-MM-DD HH:mm:ss"
              const formattedDate = newDate.format('YYYY-MM-DD HH:mm:ss');
              setCampaign((prevState) => ({
                ...prevState,
                scheduledAt: formattedDate
              }));
            }
          })
          .catch((error) => {
            // Silenciar error, no es crítico - usar fecha actual + 30 min como fallback
            console.debug("No se pudo obtener última campaña activa, usando fecha actual + 30 min:", error);
            const timezoneFallback = getCampaignTimezoneSync();
            const nowInCompanyTimezone = moment().tz(timezoneFallback);
            const newDate = nowInCompanyTimezone.add(30, 'minutes');
            // ✅ CORRECCIÓN: DateTimePickerWithTimezone espera formato "YYYY-MM-DD HH:mm:ss"
            const formattedDate = newDate.format('YYYY-MM-DD HH:mm:ss');
            setCampaign((prevState) => ({
              ...prevState,
              scheduledAt: formattedDate
            }));
          });
        return;
      }

      getCampaign(campaignId).then(({ data }) => {
        setCampaign((prev) => {
          let prevCampaignData = Object.assign({}, prev);

          // ✅ Obtener zona horaria de la empresa para campañas (desde estado sincronizado)
          const timezone = getCampaignTimezoneSync();

          // ✅ CORRECCIÓN CRÍTICA: Para campañas RECURRENTES que ya ejecutaron, usar nextScheduledAt
          // en lugar de scheduledAt para el campo de fecha programada
          const isRecurringActive = (data.tipo === 'RECURRENTE' || data.isRecurring) &&
                                    data.executionCount &&
                                    data.executionCount > 0 &&
                                    (data.status === 'PROGRAMADA' || data.status === 'ACTIVA');

          Object.entries(data).forEach(([key, value]) => {
            if (key === "scheduledAt" && value !== "" && value !== null) {
              // ✅ CORRECCIÓN: Para campañas RECURRENTES activas, guardar scheduledAt original
              // pero usar nextScheduledAt para el campo de edición
              if (isRecurringActive && data.nextScheduledAt) {
                // Guardar scheduledAt original para referencia
                prevCampaignData.originalScheduledAt = String(value);
                // Usar nextScheduledAt para el campo de edición
                prevCampaignData[key] = String(data.nextScheduledAt);
              } else {
                // ✅ CORRECCIÓN: El backend devuelve scheduledAt como string "YYYY-MM-DD HH:mm:ss"
                // en la zona horaria de la empresa. Necesitamos interpretarlo en esa zona horaria
                // y luego convertir a la zona horaria del navegador para el input datetime-local
                // ✅ CORRECCIÓN: DateTimePickerWithTimezone espera formato "YYYY-MM-DD HH:mm:ss"
                // El backend ya devuelve en ese formato, así que usamos directamente
                // El componente se encargará de la conversión interna
                prevCampaignData[key] = String(value);
              }
            } else if (key === "recurrenceEndDate" && value !== "" && value !== null) {
              // ✅ CORRECCIÓN: Para recurrenceEndDate mantenemos formato datetime-local
              // porque no usamos DateTimePickerWithTimezone para este campo
              const endDateMoment = moment.tz(String(value), 'YYYY-MM-DD HH:mm:ss', timezone);
              if (endDateMoment.isValid()) {
                prevCampaignData[key] = endDateMoment.local().format("YYYY-MM-DDTHH:mm");
              } else {
                const fallbackMoment = moment.tz(String(value), timezone);
                if (fallbackMoment.isValid()) {
                  prevCampaignData[key] = fallbackMoment.local().format("YYYY-MM-DDTHH:mm");
                } else {
                  prevCampaignData[key] = moment(value).format("YYYY-MM-DDTHH:mm");
                }
              }
            } else if (key === "tipo") {
              // ✅ FASE 4: Establecer tipo de campaña
              prevCampaignData[key] = value || (data.isRecurring ? 'RECURRENTE' : 'UNICA');
            } else if (key === "isRecurring") {
              // ✅ CORRECCIÓN: Asegurar que isRecurring sea boolean
              if (value === true || value === 1 || value === '1' || value === 'true') {
                prevCampaignData[key] = true;
              } else if (value === false || value === 0 || value === '0' || value === 'false' || value === null || value === '') {
                prevCampaignData[key] = false;
              } else {
                prevCampaignData[key] = value;
              }
            } else if (key === "recurrenceDaysOfWeek") {
              // ✅ CORRECCIÓN: Asegurar que recurrenceDaysOfWeek se cargue correctamente
              // Puede venir como null, string vacío, JSON string, o array
              if (value === null || value === undefined || value === '') {
                prevCampaignData[key] = "";
              } else if (Array.isArray(value)) {
                // Si viene como array, convertirlo a JSON string
                prevCampaignData[key] = JSON.stringify(value);
              } else if (typeof value === 'string') {
                // Si ya es un string, verificar si es JSON válido
                try {
                  const parsed = JSON.parse(value);
                  // Si es un array válido, mantenerlo como JSON string
                  if (Array.isArray(parsed)) {
                    prevCampaignData[key] = value;
                  } else {
                    // No es un array, intentar convertirlo
                    prevCampaignData[key] = JSON.stringify([parsed]);
                  }
                } catch (e) {
                  // No es JSON válido, intentar parsearlo como array de números
                  // Ejemplo: "0,1,2" o "[0,1,2]" sin comillas
                  const cleaned = value.replace(/[[\]]/g, '');
                  const numbers = cleaned.split(',').map(n => parseInt(n.trim())).filter(n => !isNaN(n));
                  if (numbers.length > 0) {
                    prevCampaignData[key] = JSON.stringify(numbers);
                  } else {
                    prevCampaignData[key] = "";
                  }
                }
              } else {
                prevCampaignData[key] = value;
              }

              // ✅ DEBUG: Log temporal para verificar carga (comentado para producción)
              // if (key === "recurrenceDaysOfWeek") {
              //   console.log('[CampaignModal] 🔍 recurrenceDaysOfWeek cargado:', {
              //     originalValue: value,
              //     tipo: typeof value,
              //     processedValue: prevCampaignData[key],
              //     parsed: prevCampaignData[key] && prevCampaignData[key] !== "" ? (() => {
              //       try {
              //         return JSON.parse(prevCampaignData[key]);
              //       } catch (e) {
              //         return "ERROR_PARSING";
              //       }
              //     })() : null
              //   });
              // }
            } else {
              prevCampaignData[key] = value === null ? "" : value;
            }
          });

          // ✅ FASE 4: Asegurar que tipo esté establecido correctamente
          // Verificar múltiples formas de determinar si es RECURRENTE
          // ✅ CORRECCIÓN CRÍTICA: Primero verificar explícitamente si NO es recurrente
          if (!prevCampaignData.tipo || prevCampaignData.tipo === '') {
            // 1. Si isRecurring es explícitamente false, es UNICA
            if (prevCampaignData.isRecurring === false || prevCampaignData.isRecurring === 0 || prevCampaignData.isRecurring === '0' || prevCampaignData.isRecurring === false) {
              prevCampaignData.tipo = 'UNICA';
            }
            // 2. Si isRecurring es explícitamente true, es RECURRENTE
            else if (prevCampaignData.isRecurring === true || prevCampaignData.isRecurring === 1 || prevCampaignData.isRecurring === '1') {
              prevCampaignData.tipo = 'RECURRENTE';
            }
            // 3. Verificar si tiene campos de recurrencia configurados
            else if (prevCampaignData.recurrenceType || prevCampaignData.recurrenceInterval) {
              prevCampaignData.tipo = 'RECURRENTE';
            }
            // 4. Por defecto, es UNICA
            else {
              prevCampaignData.tipo = 'UNICA';
            }
          }

          // ✅ CORRECCIÓN CRÍTICA: Si tipo es 'UNICA', asegurar que isRecurring sea false
          if (prevCampaignData.tipo === 'UNICA') {
            prevCampaignData.isRecurring = false;
          }
          // ✅ CORRECCIÓN: Si tipo es 'RECURRENTE', asegurar que isRecurring sea true
          else if (prevCampaignData.tipo === 'RECURRENTE') {
            prevCampaignData.isRecurring = true;
          }

          // ✅ DEBUG: Log temporal para verificar datos cargados (comentado para producción)
          // console.log('[CampaignModal] 🔍 Datos cargados de campaña:', {
          //   id: campaignId,
          //   tipo: prevCampaignData.tipo,
          //   isRecurring: prevCampaignData.isRecurring,
          //   recurrenceType: prevCampaignData.recurrenceType,
          //   recurrenceInterval: prevCampaignData.recurrenceInterval,
          //   dataFromAPI: {
          //     tipo: data.tipo,
          //     isRecurring: data.isRecurring
          //   }
          // });

          return prevCampaignData;
        });

        // Sin enableReinitialize: remontar Formik tras hidratar campaign desde API (edición)
        setTimeout(() => {
          if (isMounted.current) onFormKeyIncrement();
        }, 0);

        setCampaignAiChoice(data.promptId ? 'yes' : 'no');

        // ✅ FASE 4: Ajustar configTab según el tipo de campaña al cargar
        // Si es UNICA, configTab debe ser 0 (MENSAJE)
        // Si es RECURRENTE, configTab puede ser 0 (RECURRENCIA) o 1 (MENSAJE) según corresponda
        const loadedCampaignType = data.tipo || (data.isRecurring ? 'RECURRENTE' : 'UNICA');
        if (loadedCampaignType === 'UNICA') {
          // Para campañas UNICAS, siempre empezar en MENSAJE (índice 0)
          onSetConfigTab(0);
        } else {
          // Para campañas RECURRENTES, empezar en RECURRENCIA (índice 0)
          onSetConfigTab(0);
        }

        // ✅ CORRECCIÓN: Cargar el conteo de contactos para mostrar cálculos de tiempo
        // Prioridad 1: Usar totalContacts del backend si está disponible
        if (data.totalContacts !== undefined && data.totalContacts !== null) {
          setSelectedContactListCount(data.totalContacts);
        }
        // Prioridad 2: Usar contactListItems si están disponibles
        else if (data.contactList && data.contactList.contactListItems) {
          setSelectedContactListCount(data.contactList.contactListItems.length);
        }
        // Prioridad 3: Hacer petición para obtener el conteo (más confiable que buscar en estado)
        else if (data.contactListId) {
          getContactList(data.contactListId)
            .then(({ data: contactListData }) => {
              if (contactListData && contactListData.contactListItems) {
                setSelectedContactListCount(contactListData.contactListItems.length);
              } else if (contactListData && contactListData.contactsCount !== undefined) {
                setSelectedContactListCount(contactListData.contactsCount);
              }
            })
            .catch((err) => {
              console.warn("No se pudo cargar el conteo de contactos:", err);
            });
        }
      });
    }
  }, [campaignId, open, initialValues, companyId, campaignType]);

  const resetCampaign = () => setCampaign(makeInitialState(companyId, campaignType));

  return {
    campaign,
    setCampaign,
    resetCampaign,
    contactLists,
    setContactLists,
    whatsapps,
    tagLists,
    companyTimezone,
    setCompanyTimezone,
    promptsList,
    setPromptsList,
    file,
    selectedContactListCount,
    setSelectedContactListCount,
  };
};

export default useCampaignData;
